/**
 * What kind of device this is, for the parts of notification setup that differ
 * by platform. Only used to choose instructions and buttons -- never to decide
 * whether something is allowed, which is always feature-detected instead.
 */

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

export const isIOS =
	/iPad|iPhone|iPod/.test(ua) ||
	// iPadOS 13+ reports itself as a Mac; the touch points give it away.
	(typeof navigator !== 'undefined' && /Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

export const isAndroid = /Android/i.test(ua);

/** Running as an installed app (Home Screen / app drawer) rather than a browser tab. */
export function isStandalone(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}
	return (
		window.matchMedia?.('(display-mode: standalone)').matches ||
		// Safari's own flag, which predates display-mode support.
		(navigator as Navigator & { standalone?: boolean }).standalone === true
	);
}

/**
 * How to undo a "Block" on this device. Browsers never let a page re-ask once
 * notifications are blocked, so the only way back is through settings -- and
 * "check your browser settings" is not something most people can act on.
 */
export function unblockInstructions(): string {
	if (isAndroid) {
		return isStandalone()
			? 'Notifications are turned off for Keystone Mail. Open your phone’s Settings › Apps › Keystone Mail › Notifications and turn them on, then come back here.'
			: 'Notifications are blocked for this site. Tap the icon to the left of the address bar, then Permissions › Notifications › Allow, and reload.';
	}
	if (isIOS) {
		return 'Notifications are turned off for Keystone Mail. Open the iPhone Settings app › Notifications › Keystone Mail and turn on Allow Notifications.';
	}
	return 'Notifications are blocked for this site. Click the icon to the left of the address bar, set Notifications to Allow, and reload the page.';
}

/* ------------------------------------------------------------ install prompt */

/** Chrome/Android's install event. Not in the DOM typings. */
interface InstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredInstall: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

// Must be registered at load: the browser fires this once, early, and a
// listener added later simply never hears it.
if (typeof window !== 'undefined') {
	window.addEventListener('beforeinstallprompt', event => {
		event.preventDefault();
		deferredInstall = event as InstallPromptEvent;
		listeners.forEach(listener => listener());
	});
	window.addEventListener('appinstalled', () => {
		deferredInstall = null;
		listeners.forEach(listener => listener());
	});
}

export function canInstall(): boolean {
	return deferredInstall !== null;
}

export function onInstallChange(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/** Shows the browser's own "Install app" dialog. Must be called from a click. */
export async function promptInstall(): Promise<boolean> {
	const event = deferredInstall;
	if (!event) {
		return false;
	}
	deferredInstall = null;
	await event.prompt();
	const choice = await event.userChoice;
	listeners.forEach(listener => listener());
	return 'accepted' === choice.outcome;
}
